import axios from "axios";
import { useEffect, useState } from "react";
import { X } from "lucide-react";

export default function AddDealDrawer({
  isOpen,
  onClose,
  onAddCustomer,
}) {
  const [form, setForm] = useState({
    uniqueId: "",
    leadId: "",
    customerId: "",
    accountManager: "",
    customerName: "",
    status: "",
    customerPONumber: "",
    poDate: "",
    poValue: "",
    tax: "",
    warrantyValidity: "",
    otherBudgetEstimation: "",
    gstNumber: "",
    paymentTerms: "",
    uploadPO: null,
    emd: "",
    bankGuarantee: "",
    contactDetails: "",
    deliveryDetails: "",
    description: "",
    licenseKey: "",
  });

  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  

  useEffect(() => {
    if (isOpen) {
      getDropdowns();
    }
  }, [isOpen]);

  const getDropdowns = async () => {
    try {
      const token = localStorage.getItem("token");

      const { data } = await axios.get(
        "http://localhost:5000/api/deals/dropdowns",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        setCustomers(data.customers || []);
        setStatuses(data.statuses || []);
      }
    } catch (err) {
      console.error("Dropdown Error:", err);

      if (err.response) {
        alert(
          err.response.data.message ||
            "Unable to load dropdowns."
        );
      } else {
        alert("Unable to connect to server.");
      }
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const fields = [
    ["Unique ID", "uniqueId", "text"],
    ["Lead ID", "leadId", "text"],
    ["Customer Name", "customerName", "customer"],
    ["Status", "status", "status"],
    ["Customer PO Number", "customerPONumber", "text"],
    ["PO Date", "poDate", "date"],
    ["PO Value", "poValue", "text"],
    ["Tax", "tax", "text"],
    ["Warranty Validity", "warrantyValidity", "date"],
    ["Other Budget Estimation", "otherBudgetEstimation", "text"],
    ["GST Number", "gstNumber", "text"],
    ["Payment Terms", "paymentTerms", "text"],
    ["Upload PO", "uploadPO", "file"],
    ["EMD", "emd", "text"],
    ["Bank Guarantee", "bankGuarantee", "text"],
    ["Contact Details", "contactDetails", "text"],
    ["Delivery Details", "deliveryDetails", "textarea"],
    ["Description", "description", "textarea"],
    ["License Key", "licenseKey", "text"],
  ];

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const formData = new FormData();

      formData.append("lead_id", "");

      formData.append(
        "customer_name",
        form.customerName
      );

      formData.append(
        "status",
        form.status
      );

      formData.append(
        "customer_po_number",
        form.customerPONumber
      );

      formData.append(
        "po_date",
        form.poDate
      );

      formData.append(
        "po_value",
        form.poValue
      );

      formData.append(
        "tax",
        form.tax
      );

      formData.append(
        "warranty_validity",
        form.warrantyValidity
      );

      formData.append(
        "other_budget_estimation",
        form.otherBudgetEstimation
      );

      formData.append(
        "gst_number",
        form.gstNumber
      );

      formData.append(
        "payment_terms",
        form.paymentTerms
      );

      formData.append(
        "emd",
        form.emd
      );

      formData.append(
        "bank_guarantee",
        form.bankGuarantee
      );

      formData.append(
        "contact_detail",
        form.contactDetails
      );

      formData.append(
        "delivery_detail",
        form.deliveryDetails
      );

      formData.append(
        "description",
        form.description
      );

      formData.append(
        "license_key",
        form.licenseKey
      );

      if (form.uploadPO) {
        formData.append(
          "attachment",
          form.uploadPO
        );
      }

      const { data } = await axios.post(
        "http://localhost:5000/api/deals/add",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert(data.message);

      setForm({
        uniqueId: "",
        leadId: "",
        customerId: "",
        accountManager: "",
        customerName: "",
        status: "",
        customerPONumber: "",
        poDate: "",
        poValue: "",
        tax: "",
        warrantyValidity: "",
        otherBudgetEstimation: "",
        gstNumber: "",
        paymentTerms: "",
        uploadPO: null,
        emd: "",
        bankGuarantee: "",
        contactDetails: "",
        deliveryDetails: "",
        description: "",
        licenseKey: "",
      });

      onClose();
    } catch (err) {
      console.error("Add Deal Error:", err);

      if (err.response) {
        alert(
          err.response.data.message ||
            "Unable to save deal."
        );
      } else if (err.request) {
        alert("No response received from server.");
      } else {
        alert(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

    return (
    <div className="fixed inset-0 z-50 flex antialiased">
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/30 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="w-[65%] bg-white h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">

        {/* Header */}
        <div className="flex justify-between items-center border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-800">
            Add Deal
          </h2>

          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">

          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">
            Customer Information
          </h3>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">

            {fields.map(([label, name, type]) => {

              const isTextArea = type === "textarea";

              return (
                <div
                  key={name}
                  className={
                    isTextArea
                      ? "col-span-2"
                      : "col-span-1"
                  }
                >

                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    {label}
                  </label>

                  {/* Customer Dropdown */}

                  {type === "customer" ? (

                    <select
                      name={name}
                      value={form.customerName}
                      onChange={(e) => {

                        if (e.target.value === "__add__") {

                          if (onAddCustomer) {
                            onAddCustomer();
                          } else {
                            alert(
                              "Open Add Customer form here."
                            );
                          }

                          return;
                        }

                        handleChange(e);

                      }}
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 cursor-pointer"
                    >

                      <option value="">
                        Select Customer
                      </option>

                      {customers.map((customer) => (
                        <option
                          key={customer}
                          value={customer}
                        >
                          {customer}
                        </option>
                      ))}

                      <option value="__add__">
                        ➕ Add Customer
                      </option>

                    </select>

                  ) : type === "status" ? (

                    <select
  name="status"
  value={form.status}
  onChange={handleChange}
  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 cursor-pointer"
>
  <option value="">
    Select Status
  </option>

  {statuses.map((status) => (
    <option
      key={status.code_id}
      value={status.code_id}
    >
      {status.code_short_description}
    </option>
  ))}
</select>

                  ) : isTextArea ? (

                    <textarea
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      rows={3}
                      placeholder={`Enter ${label.toLowerCase()} details...`}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 resize-y"
                    />

                  ) : (

                    <input
                      type={type}
                      name={name}
                      value={
                        type !== "file"
                          ? form[name]
                          : undefined
                      }
                      onChange={handleChange}
                      className={`w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 ${
                        type === "file"
                          ? "file:mr-4 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                          : ""
                      }`}
                    />

                  )}

                </div>
              );

            })}

          </div>

        </div>

        {/* Footer */}

        <div className="border-t border-slate-200 px-6 py-3.5 flex justify-end gap-2.5 bg-white">

          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-5 py-2 text-white font-semibold rounded-lg text-sm transition shadow-sm ${
              loading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Saving..." : "Save Deal"}
          </button>

        </div>

      </div>
    </div>
  );
}

